import { observer } from "mobx-react-lite"
import { useCallback, useRef, useState } from "react"
import { thumbUrl } from "../lib/api.js"
import { useStore } from "../stores/StoreProvider.js"

interface GridItemProps {
  id: string
  index: number
  onOpen: (index: number) => void
  editMode: boolean
  onDragStart: (index: number) => void
  onDragOver: (index: number) => void
  onDragEnd: () => void
  isDragOver: boolean
}

const GridItem = observer(function GridItem({
  id,
  index,
  onOpen,
  editMode,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragOver,
}: GridItemProps) {
  const [loaded, setLoaded] = useState(false)
  const loadStart = useRef(performance.now())
  const handleLoad = useCallback(() => {
    setLoaded(true)
  }, [])
  // If image loads within 50ms it was cached — skip fade animation
  const wasCached = loaded && performance.now() - loadStart.current < 50

  return (
    <div
      className={`gallery-grid-item${!loaded ? " skeleton" : ""}${isDragOver ? " ring-2 ring-tg-accent" : ""}${editMode ? " cursor-grab active:cursor-grabbing edit-mode-item" : ""}`}
      style={editMode ? { animationDelay: `${(index % 5) * 60}ms` } : undefined}
      onClick={() => !editMode && onOpen(index)}
      draggable={editMode}
      onDragStart={(e) => {
        if (!editMode) return
        e.dataTransfer.effectAllowed = "move"
        onDragStart(index)
      }}
      onDragOver={(e) => {
        if (!editMode) return
        e.preventDefault()
        onDragOver(index)
      }}
      onDrop={(e) => {
        e.preventDefault()
        onDragEnd()
      }}
      onDragEnd={onDragEnd}
    >
      <img
        src={thumbUrl(id, 400, 400)}
        alt=""
        loading="lazy"
        draggable={false}
        className={loaded && !wasCached ? "img-fade-in" : ""}
        style={loaded ? undefined : { opacity: 0 }}
        onLoad={handleLoad}
      />
    </div>
  )
})

interface GalleryGridProps {
  mediaIds: string[]
  editMode?: boolean
  galleryId?: string
}

export const GalleryGrid = observer(function GalleryGrid({
  mediaIds,
  editMode = false,
  galleryId,
}: GalleryGridProps) {
  const { viewerStore, galleryStore } = useStore()
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  const handleOpen = (index: number) => {
    viewerStore.open(mediaIds, index)
  }

  const handleDragEnd = () => {
    if (dragFrom !== null && dragOver !== null && dragFrom !== dragOver && galleryId) {
      const reordered = [...mediaIds]
      const [moved] = reordered.splice(dragFrom, 1) as [string]
      reordered.splice(dragOver, 0, moved)
      void galleryStore.reorderMedia(galleryId, reordered)
    }
    setDragFrom(null)
    setDragOver(null)
  }

  return (
    <div className="gallery-grid">
      {mediaIds.map((id, i) => (
        <GridItem
          key={id}
          id={id}
          index={i}
          onOpen={handleOpen}
          editMode={editMode}
          onDragStart={setDragFrom}
          onDragOver={setDragOver}
          onDragEnd={handleDragEnd}
          isDragOver={dragOver === i}
        />
      ))}
    </div>
  )
})
